import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type ConversationInteractionRequest,
  type ConversationInteractionSubmission,
  type ConversationMessagePart,
} from '@confluent/shared/lib/conversation-interactions';
import { conversationsApi } from '../../services/api/conversations.api.js';
import { BehindTheScenes } from './cards/BehindTheScenes.js';
import { useEntityOpener } from './entities/entity-opener.js';
import { Button } from '../ui/Button.js';
import { AgentSuggestionPill } from './AgentSuggestionPill.js';
import { DisclosureFooter } from './DisclosureFooter.js';
import { CardPart } from './cards/CardPart.js';
import { SuggestionChips } from './cards/SuggestionChips.js';
import { emphasisClass, statusClass, renderCell, getProgressStatusLabel } from './conversation-interaction-helpers.js';
import { InteractionCard } from './InteractionCard.js';

// Re-exported so existing importers and tests keep their entry point after the split.
export {
  buildInteractionActionDisplayText,
  buildInteractionDisplayText,
  getInteractionActionPendingText,
  getInteractionResolutionMeta,
  getProgressStatusLabel,
  isActionableConversationAction,
  prepareInteractionSubmission,
} from './conversation-interaction-helpers.js';
import {
  getConversationQueryKey,
  listConversationMessagesQueryKey,
  listConversationsQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { DataTable, type DataTableColumn } from '../ui/DataTable.js';

export function ConversationMessageParts({
  conversationId,
  sourceMessageId,
  parts,
  disabled,
  onError,
  onSubmitted,
  surface = 'detailed',
}: {
  conversationId: string | number;
  sourceMessageId: string;
  parts: ConversationMessagePart[];
  disabled?: boolean;
  onError?: (message: string) => void;
  onSubmitted?: () => void;
  /** `simple` is the simplified landing: no Switch/Spawn pills, cards carry the affordances. */
  surface?: 'simple' | 'detailed';
}) {
  const queryClient = useQueryClient();
  // A record a part links to opens inside the conversation where the surface has the records panel (owner 2026-10-31).
  const opener = useEntityOpener();
  const conversationKey = String(conversationId);
  const interactionMutation = useMutation({
    mutationFn: async (input: {
      interactionId: string;
      request: ConversationInteractionRequest;
      submission: ConversationInteractionSubmission;
    }) => {
      await conversationsApi.submitInteraction(conversationKey, input.interactionId, {
        action: input.submission.action,
        values: input.submission.values,
        displayText: input.submission.displayText,
        idempotencyKey: input.submission.idempotencyKey,
        surface,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listConversationMessagesQueryKey({ path: { id: conversationKey } }) });
      queryClient.invalidateQueries({ queryKey: getConversationQueryKey({ path: { id: conversationKey } }) });
      queryClient.invalidateQueries({
        queryKey: listConversationsQueryKey({ query: { parentConversationId: conversationKey } }),
      });
      onSubmitted?.();
    },
    onError: (err) => {
      onError?.((err as Error).message ?? 'Failed to submit response.');
    },
  });

  return (
    <div className="space-y-2">
      {parts.map((part, partIndex) => {
        if (part.type === 'text') {
          return (
            <p key={`${part.type}-${partIndex}`} className="text-sm whitespace-pre-wrap wrap-break-word">
              {part.content}
            </p>
          );
        }
        if (part.type === 'section') {
          return (
            <section key={`${part.type}-${partIndex}`} className="space-y-2">
              <div>
                <p className="text-sm font-semibold text-heading">{part.title}</p>
                {part.body && <p className="mt-1 text-sm text-body whitespace-pre-wrap">{part.body}</p>}
              </div>
              {part.items.length > 0 && (
                <dl className="grid gap-2">
                  {part.items.map((item, itemIndex) => (
                    <div key={`${item.label}-${itemIndex}`} className="rounded-md border bg-surface px-2.5 py-2">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted">{item.label}</dt>
                      <dd className={`mt-0.5 text-sm ${emphasisClass[item.emphasis ?? 'normal']}`}>{item.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          );
        }
        if (part.type === 'table') {
          return (
            <section key={`${part.type}-${partIndex}`} className="space-y-2">
              {part.title && <p className="text-sm font-semibold text-heading">{part.title}</p>}
              <DataTable
                framed
                aria-label={part.title ?? 'Table'}
                rows={part.rows ?? []}
                rowKey={(_row, index) => index}
                columns={part.columns.map(
                  (column): DataTableColumn<Record<string, string | number | boolean | null>> => ({
                    key: column.id,
                    header: column.label,
                    ...(column.align ? { align: column.align } : {}),
                    render: (row) => renderCell(row[column.id]),
                  }),
                )}
              />
            </section>
          );
        }
        if (part.type === 'checklist') {
          return (
            <section key={`${part.type}-${partIndex}`} className="space-y-2">
              {part.title && <p className="text-sm font-semibold text-heading">{part.title}</p>}
              <div className="space-y-1.5">
                {part.items.map((item, itemIndex) => (
                  <div key={`${item.label}-${itemIndex}`} className="rounded-md border bg-surface px-2.5 py-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-heading">{item.label}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] ${statusClass[item.status ?? 'pending']}`}>
                        {getProgressStatusLabel(item.status ?? 'pending')}
                      </span>
                    </div>
                    {item.detail && <p className="mt-1 text-xs text-muted">{item.detail}</p>}
                  </div>
                ))}
              </div>
            </section>
          );
        }
        if (part.type === 'proposal') {
          return (
            <section key={`${part.type}-${partIndex}`} className="rounded-lg border bg-surface px-3 py-3 space-y-3">
              <div>
                <p className="text-sm font-semibold text-heading">{part.title}</p>
                {part.summary && <p className="mt-1 text-sm text-body whitespace-pre-wrap">{part.summary}</p>}
                {part.rationale && <p className="mt-1 text-xs text-muted whitespace-pre-wrap">{part.rationale}</p>}
              </div>
              {part.steps.length > 0 && (
                <ol className="space-y-2">
                  {part.steps.map((step, stepIndex) => (
                    <li key={`${step.title}-${stepIndex}`} className="flex gap-2">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/10 text-[11px] font-medium text-accent-text">
                        {stepIndex + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-heading">{step.title}</p>
                        {step.detail && <p className="text-xs text-muted">{step.detail}</p>}
                        {step.output && <p className="text-xs text-body">Output: {step.output}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          );
        }
        if (part.type === 'interaction_request') {
          return (
            <InteractionCard
              key={`${part.type}-${part.interactionId ?? partIndex}`}
              part={part}
              sourceMessageId={sourceMessageId}
              disabled={disabled || interactionMutation.isPending}
              onSubmit={(input) => interactionMutation.mutate(input)}
            />
          );
        }
        if (part.type === 'artifact_ref') {
          const routeByKind: Record<string, string> = {
            artifact: `/artifacts/${part.artifactId}`,
            conversation: `/conversations/${part.artifactId}`,
            task: `/tasks/${part.artifactId}`,
            project: `/projects/${part.artifactId}`,
            workflow: `/workflows/${part.artifactId}`,
          };
          const route = routeByKind[part.kind];
          const fallbackSummary: Record<string, string> = {
            artifact: 'Open artifact',
            conversation: 'Open linked conversation',
            task: 'Open task workspace',
            project: 'Open project workspace',
            workflow: 'Open workflow',
          };
          const linked = { entity: part.kind, id: String(part.artifactId) };
          if (opener?.canOpen(linked)) {
            return (
              <Button
                key={`${part.type}-${part.artifactId}`}
                variant="ghost"
                onClick={() => opener.open(linked)}
                className="block h-auto w-full rounded-lg border bg-surface px-3 py-2 text-left font-normal hover:border-accent"
              >
                <p className="text-sm font-medium text-heading">{part.title}</p>
                <p className="text-xs text-muted">{part.summary ?? fallbackSummary[part.kind] ?? 'Open linked item'}</p>
              </Button>
            );
          }
          if (route) {
            return (
              <Link
                key={`${part.type}-${part.artifactId}`}
                to={route}
                className="block rounded-lg border bg-surface px-3 py-2 hover:border-accent"
              >
                <p className="text-sm font-medium text-heading">{part.title}</p>
                <p className="text-xs text-muted">{part.summary ?? fallbackSummary[part.kind] ?? 'Open linked item'}</p>
              </Link>
            );
          }
          return (
            <div key={`${part.type}-${part.artifactId}`} className="rounded-lg border bg-surface px-3 py-2">
              <p className="text-sm font-medium text-heading">{part.title}</p>
              <p className="text-xs text-muted">{part.summary ?? part.kind}</p>
            </div>
          );
        }
        if (part.type === 'progress') {
          return (
            <div key={`${part.type}-${partIndex}`} className="rounded-lg border bg-surface px-3 py-2">
              <p className="text-xs font-medium text-heading">{part.label}</p>
              <p className="text-xs text-muted">{part.detail ?? getProgressStatusLabel(part.status)}</p>
            </div>
          );
        }
        if (part.type === 'warning') {
          return (
            <div
              key={`${part.type}-${partIndex}`}
              className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2"
            >
              <p className="text-sm text-heading">{part.message}</p>
              {part.recommendation && <p className="mt-1 text-xs text-muted">{part.recommendation}</p>}
            </div>
          );
        }
        if (part.type === 'card') {
          return (
            <CardPart
              key={`${part.type}-${part.card.id}-${partIndex}`}
              card={part.card}
              conversationId={conversationKey}
              disabled={disabled}
            />
          );
        }
        if (part.type === 'behind_the_scenes') {
          return <BehindTheScenes key={`${part.type}-${partIndex}`} items={part.items} />;
        }
        if (part.type === 'suggestions') {
          return (
            <SuggestionChips
              key={`${part.type}-${partIndex}`}
              items={part.items}
              conversationId={conversationKey}
              disabled={disabled}
            />
          );
        }
        if (part.type === 'agent_suggestion') {
          return (
            <AgentSuggestionPill
              key={`agent-suggestion-${part.personaId}`}
              conversationId={conversationKey}
              part={part}
            />
          );
        }
        if (part.type === 'disclosure') {
          return (
            <DisclosureFooter
              key={`${part.type}-${partIndex}`}
              disclosure={part.disclosure}
              personaName={part.personaName}
            />
          );
        }
        return null;
      })}
    </div>
  );
}
